import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LocationProvider } from "@/context/LocationContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { MobileNav } from "@/components/layout/MobileNav";
import { Loader } from "@/components/shared";

import Home from "@/pages/Home";
import SearchPage from "@/pages/SearchPage";
import PartDetail from "@/pages/PartDetail";
import YardsPage from "@/pages/YardsPage";
import YardDetail from "@/pages/YardDetail";
import Login from "@/pages/Login";
import RegisterCustomer from "@/pages/RegisterCustomer";
import RegisterSeller from "@/pages/RegisterSeller";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import RequestPart from "@/pages/RequestPart";
import MyRequests from "@/pages/MyRequests";
import Orders from "@/pages/Orders";
import Notifications from "@/pages/Notifications";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import TapReturn from "@/pages/TapReturn";
import PolicyPage from "@/pages/PolicyPage";
import { AboutPage, FAQPage } from "@/pages/InfoPages";
import { StaticPage } from "@/pages/StaticPages";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pb-20 md:pb-0">{children}</main>
      <Footer />
      <MobileNav />
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <LocationProvider>
            <BrowserRouter>
              <RoutedApp />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </LocationProvider>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

function RoutedApp() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/search" element={<Shell><SearchPage /></Shell>} />
      <Route path="/part/:id" element={<Shell><PartDetail /></Shell>} />
      <Route path="/yards" element={<Shell><YardsPage /></Shell>} />
      <Route path="/yard/:id" element={<Shell><YardDetail /></Shell>} />
      <Route path="/login" element={<Shell><Login /></Shell>} />
      <Route path="/register" element={<Shell><RegisterCustomer /></Shell>} />
      <Route path="/join-seller" element={<Shell><RegisterSeller /></Shell>} />
      <Route path="/verify-email" element={<Shell><VerifyEmail /></Shell>} />
      <Route path="/forgot-password" element={<Shell><ForgotPassword /></Shell>} />
      <Route path="/how-it-works" element={<Shell><StaticPage type="how" /></Shell>} />
      <Route path="/contact" element={<Shell><StaticPage type="contact" /></Shell>} />
      <Route path="/about" element={<Shell><AboutPage /></Shell>} />
      <Route path="/faq" element={<Shell><FAQPage /></Shell>} />
      <Route path="/policy/:slug" element={<Shell><PolicyPage /></Shell>} />
      <Route path="/privacy" element={<Navigate to="/policy/privacy" replace />} />
      <Route path="/terms" element={<Navigate to="/policy/terms" replace />} />
      <Route path="/payment/tap-return" element={<Shell><TapReturn /></Shell>} />
      <Route path="/orders" element={<Protected roles={["customer"]}><Shell><Orders /></Shell></Protected>} />
      <Route path="/request-part" element={<Shell><RequestPart /></Shell>} />
      <Route path="/my-requests" element={<Protected roles={["customer"]}><Shell><MyRequests /></Shell></Protected>} />
      <Route path="/notifications" element={<Protected><Shell><Notifications /></Shell></Protected>} />
      <Route path="/seller" element={<Protected roles={["seller"]}><Shell><SellerDashboard /></Shell></Protected>} />
      <Route path="/admin-login" element={<Shell><AdminLogin /></Shell>} />
      <Route path="/admin" element={<Protected roles={["admin"]}><Shell><AdminDashboard /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
